import React from 'react';

interface Message {
  message_number: number;
  content: string;
  character_count: number;
  delay_days?: number;
}

interface MessageSequenceDisplayProps {
  messages: Message[];
  title?: string;
  className?: string;
  showBackground?: boolean;
}

/**
 * Reusable component for displaying campaign message sequences
 * Used in CampaignApprovals, CSRCampaignDetails, and other places where message sequences need to be shown
 */
const MessageSequenceDisplay: React.FC<MessageSequenceDisplayProps> = ({
  messages,
  title = "Message Sequence",
  className = "",
  showBackground = true
}) => {
  if (!messages || messages.length === 0) {
    return null;
  }

  const containerClass = showBackground 
    ? `p-4 bg-gray-50 rounded-lg ${className}`
    : className;

  return (
    <div className={containerClass}>
      <h4 className="font-medium text-gray-900 mb-3">{title}</h4>
      <div className="space-y-3">
        {messages.map((message, index) => (
          <div key={index} className="bg-white p-3 rounded border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                LinkedIn Message {message.message_number}
              </span>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <span>{message.character_count} chars</span>
                {message.delay_days && message.delay_days > 0 && (
                  <span className="flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {message.delay_days} {message.delay_days === 1 ? 'day' : 'days'} delay
                  </span>
                )}
              </div>
            </div>
            <div className="text-sm text-gray-700 whitespace-pre-wrap">
              {message.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MessageSequenceDisplay;